import React, { useState, useEffect } from "react";
import axios from "axios"
import api from '../utils/api';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  CircularProgress,
} from "@mui/material";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  createColumnHelper,
  flexRender,
} from "@tanstack/react-table";

// Transformation des données de mergedInventory en objet exploitable pour la table
// Ici, nous utilisons : productName pour le titre, asin, (ean à défaut de champs, on met "N/A"), totalQuantity pour stock, price pour le prix.
const transformMergedInventory = (mergedInventory) => {
  return mergedInventory.map((item) => {
    const title = item.productName || "N/A";
    const asin = item.asin || "N/A";
    // Si un champ ean existe, l'utiliser ; sinon "N/A"
    const ean = item.ean || "N/A";
    const quantity = item.totalQuantity || 0;
    // Le champ "price" est désormais issu du merge (price déjà défini)
    const price = item.price !== undefined ? item.price : "N/A";
    const amazonLink =
      asin !== "N/A" ? `https://www.amazon.fr/dp/${asin}` : "#";
    return { title, asin, ean, quantity, price, amazonLink };
  });
};

// Filtre global : recherche dans Titre, ASIN et EAN
const globalFilterFn = (row, columnId, filterValue) => {
  const { title, asin, ean } = row.original;
  return (
    title.toLowerCase().includes(filterValue.toLowerCase()) ||
    asin.toLowerCase().includes(filterValue.toLowerCase()) ||
    ean.toLowerCase().includes(filterValue.toLowerCase())
  );
};

// Filtre personnalisé pour la colonne "quantity" (ex: ">2", "<5", "3")
const numericFilterFn = (row, columnId, filterValue) => {
  const rowValue = Number(row.getValue(columnId));
  if (!filterValue) return true;
  const match = filterValue.trim().match(/^([><=]?)(\d+)$/);
  if (!match) return true; // Saisie non conforme : ne filtre pas
  const operator = match[1];
  const number = Number(match[2]);
  if (operator === ">") return rowValue > number;
  if (operator === "<") return rowValue < number;
  return rowValue === number;
};

function AmazonInventory() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");

  const fetchMergedInventory = async () => {
    setLoading(true);
    try {
      const response = await api.get("/amazon/mergedInventory"
      );
      const items = response.data.mergedInventory || [];
      const transformed = transformMergedInventory(items);
      setData(transformed);
    } catch (error) {
      console.error(
        "Erreur lors de la récupération de l'inventaire fusionné:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMergedInventory();
  }, []);

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("title", {
      header: "Titre",
      cell: (info) => info.getValue(),
      enableColumnFilter: true,
    }),
    columnHelper.accessor("asin", {
      header: "ASIN",
      cell: (info) => info.getValue(),
      enableColumnFilter: true,
    }),
    columnHelper.accessor("ean", {
      header: "EAN",
      cell: (info) => info.getValue(),
      enableColumnFilter: true,
    }),
    columnHelper.accessor("quantity", {
      header: "Nombre restant",
      cell: (info) => info.getValue(),
      enableColumnFilter: true,
      filterFn: numericFilterFn,
    }),
    columnHelper.accessor("price", {
      header: "Prix actuel",
      cell: (info) => info.getValue(),
      enableColumnFilter: true,
    }),
    columnHelper.accessor("amazonLink", {
      header: "Lien Amazon",
      cell: (info) => (
        <Link href={info.getValue()} target="_blank" rel="noopener noreferrer">
          Voir sur Amazon
        </Link>
      ),
      enableColumnFilter: false,
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { globalFilter },
    globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: { pageSize: 30 },
      sorting: [{ id: "quantity", desc: true }],
    },
  });

  return (
    <div style={{ padding: "20px" }}>
      <Typography variant="h4" gutterBottom>
        Inventaire des produits fusionné
      </Typography>
      <Button
        variant="contained"
        onClick={fetchMergedInventory}
        style={{ marginBottom: "20px" }}
      >
        Rafraîchir
      </Button>
      <TextField
        label="Recherche (Titre, ASIN, EAN)"
        variant="outlined"
        value={globalFilter}
        onChange={(e) => setGlobalFilter(e.target.value)}
        style={{ marginBottom: "20px", width: "300px" }}
      />
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableCell key={header.id}>
                        {header.isPlaceholder ? null : (
                          <div>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {header.column.columnDef.enableColumnFilter ? (
                              <div>
                                <input
                                  type="text"
                                  value={header.column.getFilterValue() || ""}
                                  onChange={(e) =>
                                    header.column.setFilterValue(e.target.value)
                                  }
                                  placeholder="Filtrer..."
                                  style={{ marginTop: "4px", width: "100%" }}
                                />
                              </div>
                            ) : null}
                          </div>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <div
            style={{
              marginTop: "20px",
              display: "flex",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <Button
              variant="contained"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Page précédente
            </Button>
            <span>
              Page {table.getState().pagination.pageIndex + 1} sur{" "}
              {table.getPageCount()}
            </span>
            <Button
              variant="contained"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Page suivante
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default AmazonInventory;
